import { Box, Typography, Paper, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EvaluationSelector } from "./EvaluationSelector";
import { EvaluationSummary } from "./EvaluationSummary";
import { EvaluationSectionsList } from "./EvaluationSectionsList";
import { EvaluationSkeleton } from "./EvaluationSkeleton";
import {
  CategoryDetail,
  EvaluationResponse,
  SingleEvaluationResponse,
} from "../../Models/evaluation";
import { SectionWithKey } from "./EvaluationSectionsList";
import colors from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

export interface ComplianceEvaluationsCardProps {
  evaluations: EvaluationResponse[];
  selectedEvaluation?: SingleEvaluationResponse;
  isLoadingSelectedEvaluation: boolean;
  onSelectEvaluation: (evaluationId: number) => void;
  onDownloadEvaluation?: (evaluationId: number) => void;
  onDeleteEvaluation?: (evaluationId: number) => void;
  onSaveFeedback: (
    sectionId: string,
    responses: Record<string, string>
  ) => void;
}

export function ComplianceEvaluationsCard({
  evaluations,
  selectedEvaluation,
  isLoadingSelectedEvaluation,
  onSelectEvaluation,
  onDownloadEvaluation,
  onDeleteEvaluation,
  onSaveFeedback,
}: ComplianceEvaluationsCardProps) {
  const { t } = useTranslation("singleCompanyComponent");
  const { colors: workspace } = useWorkspaceColors();
  const parsedSections: SectionWithKey[] = Object.entries(
    selectedEvaluation?.evaluation || {}
  )
    .filter(
      ([key]) =>
        !["more_info", "questions", "summary", "summary_rating"].includes(key)
    )
    .map(([key, value]) => ({
      key,
      category: value as CategoryDetail,
    })) as SectionWithKey[];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        width: "100%",
        overflowX: "auto",
        wordBreak: "break-word",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: workspace.primary_dark,
          mb: 2,
          fontSize: "1.1rem",
        }}
      >
        {t("complianceEvaluations.title")}
      </Typography>

      <EvaluationSelector
        evaluations={evaluations}
        selectedEvaluation={selectedEvaluation}
        onSelectEvaluation={onSelectEvaluation}
        onDownloadEvaluation={onDownloadEvaluation}
        onDeleteEvaluation={onDeleteEvaluation}
      />

      <Divider sx={{ mb: 3 }} />

      {isLoadingSelectedEvaluation && <EvaluationSkeleton />}

      {selectedEvaluation && !isLoadingSelectedEvaluation && (
        <Box>
          <EvaluationSummary evaluation={selectedEvaluation} />

          <EvaluationSectionsList
            sections={parsedSections}
            questions={selectedEvaluation.evaluation?.questions || []}
            answers={selectedEvaluation.answers || []}
            onSaveFeedback={onSaveFeedback}
          />
        </Box>
      )}
    </Paper>
  );
}
